// Trick tables and combo scoring, modeled on THPS: the face button picks the
// trick family, the direction held when you press it picks the trick.

export type Dir = "n" | "u" | "d" | "l" | "r";
/** Eight directions plus neutral: grinds read diagonals too (Crooked, Smith...). */
export type Dir8 = Dir | "ul" | "ur" | "dl" | "dr";

/**
 * "underflip": a kickflip one way, then flipped back the other (Kickflip Underflip).
 * The diagonals mix a flip with a shove-it: "treflip" (360 Flip) a kickflip
 * plus a 360 shove, "hardflip" a kickflip plus a front shove, "varialheel" a
 * heelflip plus a front shove.
 */
export type FlipAnim =
  | "kickflip"
  | "heelflip"
  | "varial"
  | "impossible"
  | "shove"
  | "underflip"
  | "treflip"
  | "hardflip"
  | "varialheel";

export interface FlipTrick {
  name: string;
  points: number;
  anim: FlipAnim;
  /** Seconds the board takes to complete the trick. Land before it ends and you bail. */
  duration: number;
  /** Shove-its: half turns in the single (a 360 Shove-It is 2). Defaults to 1. */
  halfTurns?: number;
}

export interface GrabTrick {
  name: string;
  points: number;
  /** Extra points per second the grab is held. */
  perSecond: number;
}

export type GrindStyle =
  | "fiftyfifty"
  | "nosegrind"
  | "fiveo"
  | "boardslide"
  | "lipslide"
  | "crooked"
  | "overcrook"
  | "smith"
  | "feeble"
  | "darkslide";

export interface GrindTrick {
  name: string;
  points: number;
  perSecond: number;
  style: GrindStyle;
}

/**
 * Flip button + direction, read 8 ways, laid out like THPS 2-4's defaults
 * (#85): left (or no direction) Kickflip, right Heelflip, up Impossible, down
 * Pop Shove-It, and the flip-and-shove combos on the diagonals. The 360
 * Shove-It is a double-tapped Pop Shove-It (`flipVariant`).
 */
export const FLIP_TRICKS: Record<Dir8, FlipTrick> = {
  n: { name: "Kickflip", points: 100, anim: "kickflip", duration: 0.42 },
  l: { name: "Kickflip", points: 100, anim: "kickflip", duration: 0.42 },
  r: { name: "Heelflip", points: 100, anim: "heelflip", duration: 0.42 },
  u: { name: "Impossible", points: 150, anim: "impossible", duration: 0.5 },
  d: { name: "Pop Shove-It", points: 100, anim: "shove", duration: 0.36 },
  ul: { name: "Hardflip", points: 175, anim: "hardflip", duration: 0.5 },
  ur: { name: "360 Flip", points: 200, anim: "treflip", duration: 0.54 },
  dl: { name: "Varial Kickflip", points: 150, anim: "varial", duration: 0.5 },
  dr: { name: "Varial Heelflip", points: 175, anim: "varialheel", duration: 0.5 },
};

/** Most board rotations you can tap into one flip (a Triple). */
export const MAX_FLIP_ROTATIONS = 3;
/** Longest gap (seconds) between flip taps that still upgrades the flip in progress. */
export const FLIP_TAP_GAP = 0.2;
/** Points and duration of a flip by rotation count: single, double, triple. */
const FLIP_POINTS_SCALE = [1, 2.5, 4];
const FLIP_TIME_SCALE = [1, 1.5, 2];

export interface FlipVariant extends FlipTrick {
  rotations: number;
  /** Times the animation turns the board: the rotations, plus a 360 Shove-It's head start. */
  turns: number;
}

/**
 * The Double / Triple version of a flip (tap the flip button two or three
 * times). A shove-it is named by board rotation: 360, 540 Shove-It.
 */
export function flipVariant(trick: FlipTrick, rotations: number): FlipVariant {
  const n = Math.max(1, Math.min(MAX_FLIP_ROTATIONS, Math.round(rotations)));
  const turns = trick.anim === "shove" ? n + (trick.halfTurns ?? 1) - 1 : n;
  if (n === 1) return { ...trick, rotations: 1, turns };
  const name = trick.anim === "shove" ? `${turns * 180} Shove-It` : `${n === 2 ? "Double" : "Triple"} ${trick.name}`;
  return {
    ...trick,
    name,
    points: trick.points * FLIP_POINTS_SCALE[n - 1]!,
    duration: trick.duration * FLIP_TIME_SCALE[n - 1]!,
    rotations: n,
    turns,
  };
}

export const GRAB_TRICKS: Record<Dir, GrabTrick> = {
  n: { name: "Method", points: 250, perSecond: 300 },
  u: { name: "Nosegrab", points: 200, perSecond: 250 },
  d: { name: "Tailgrab", points: 200, perSecond: 250 },
  l: { name: "Indy", points: 200, perSecond: 250 },
  r: { name: "Melon", points: 200, perSecond: 250 },
};

/**
 * Specials (THPS): only while the special meter is full, two quick stick
 * presses then a button. The table covers each kind a special can be:
 *
 * - `grab`: held like a grab but a big, long pose; let go in time to get
 *   back on the board (Christ Air).
 * - `spin`: runs by itself for `duration`, turning its own `spin` (and
 *   going upside down if `invert`) while holding the board; land before
 *   it's done and you bail (McTwist, 900). It needs a big air.
 * - `flip`: a board flip that takes longer than any normal one and never
 *   upgrades to a Double (Kickflip Underflip).
 * - `grind`: picked instead of the stick's grind when locking onto a rail
 *   (Darkslide).
 *
 * The button says where it can happen: grab and flip in the air, grind on
 * the way onto a rail. Lip and manual specials are still to come.
 */
interface SpecialBase {
  name: string;
  points: number;
  seq: [Dir, Dir];
}

export interface SpecialGrab extends SpecialBase, GrabTrick {
  kind: "grab";
  button: "grab";
}

export interface SpecialSpin extends SpecialBase {
  kind: "spin";
  button: "grab";
  /** Seconds it takes; land sooner and you bail. */
  duration: number;
  /** Degrees it turns by itself, backside. Not counted as the air's spin (the name says it). */
  spin: number;
  /** Flips upside down on the way round (McTwist). */
  invert: boolean;
  /** Whole flips head over heels on the way (the bike's Flair, #138): > 0 backward, < 0 forward. */
  flips: number;
  /** Which grab the hand holds (a GRAB_TRICKS name, for the pose); null keeps the hands where they are (on the bike's bars). */
  hand: string | null;
}

export interface SpecialFlip extends SpecialBase, FlipTrick {
  kind: "flip";
  button: "flip";
}

export interface SpecialGrind extends SpecialBase, GrindTrick {
  kind: "grind";
  button: "grind";
}

export type SpecialTrick = SpecialGrab | SpecialSpin | SpecialFlip | SpecialGrind;

export const SPECIAL_TRICKS: readonly SpecialTrick[] = [
  // Arms out in a cross, the board held up at arm's length in one hand.
  { kind: "grab", name: "Christ Air", points: 1500, perSecond: 500, seq: ["u", "d"], button: "grab" },
  // Inverted 540 on vert, board held by the toe edge.
  {
    kind: "spin",
    name: "McTwist",
    points: 2000,
    seq: ["r", "d"],
    button: "grab",
    duration: 0.8,
    spin: 540,
    invert: true,
    flips: 0,
    hand: "Melon",
  },
  // Two and a half turns: only a big vert air has time for it.
  { kind: "spin", name: "900", points: 3000, seq: ["l", "d"], button: "grab", duration: 1, spin: 900, invert: false, flips: 0, hand: "Indy" },
  { kind: "flip", name: "Kickflip Underflip", points: 1500, seq: ["l", "d"], button: "flip", anim: "underflip", duration: 0.55 },
  // Flipped over, riding the rail on the grip tape.
  { kind: "grind", name: "Darkslide", points: 1250, perSecond: 500, seq: ["l", "d"], button: "grind", style: "darkslide" },
];

/**
 * Each special's two stick taps by name (#135): the player's, from Settings,
 * or the defaults (`seq` in SPECIAL_TRICKS, every one with a down in it).
 * The button stays the special's own: a grab special is pulled with grab.
 */
export type SpecialSeqs = Readonly<Record<string, readonly [Dir, Dir]>>;

export const DEFAULT_SPECIAL_SEQS: SpecialSeqs = Object.fromEntries(SPECIAL_TRICKS.map((s) => [s.name, s.seq]));

/**
 * THPS 3/4 ollie variants: two quick stick taps while crouched, then let go
 * of ollie. Each pops a bit higher than a plain ollie and scores as the air's
 * first trick (so a spin names it: "360 Boneless").
 */
export type PopVariantId = "boneless" | "fastplant";

export interface PopVariant {
  id: PopVariantId;
  name: string;
  points: number;
  seq: [Dir, Dir];
  /** Multiplies the ollie's base pop. */
  pop: number;
}

export const POP_VARIANTS: readonly PopVariant[] = [
  // Front foot planted, back hand grabs the board and jumps it up.
  { id: "boneless", name: "Boneless", points: 150, seq: ["u", "u"], pop: 1.25 },
  // Back foot planted, front hand grabs the nose.
  { id: "fastplant", name: "Fastplant", points: 150, seq: ["d", "d"], pop: 1.15 },
];

/** Seconds after the second stick tap that letting go of ollie still pops the variant. */
export const POP_VARIANT_WINDOW = 0.35;

/** The pop variant for a two-tap stick sequence, if there is one. */
export function popVariantFor(seq: [Dir, Dir] | null): PopVariant | null {
  if (!seq) return null;
  return POP_VARIANTS.find((v) => v.seq[0] === seq[0] && v.seq[1] === seq[1]) ?? null;
}

/** Seconds after the second stick press that the face button still pulls the special. */
export const SPECIAL_WINDOW = 0.3;

/** The special for a two-press stick sequence and face button, if there is one, on these sequences, from this table (a ride's, #138). */
export function specialTrick(
  seq: [Dir, Dir] | null,
  button: SpecialTrick["button"],
  seqs: SpecialSeqs = DEFAULT_SPECIAL_SEQS,
  table: readonly SpecialTrick[] = SPECIAL_TRICKS,
): SpecialTrick | null {
  if (!seq) return null;
  return (
    table.find((s) => {
      const own = seqs[s.name] ?? s.seq;
      return s.button === button && own[0] === seq[0] && own[1] === seq[1];
    }) ?? null
  );
}

export const GRIND_TRICKS: Record<Dir8, GrindTrick> = {
  n: { name: "50-50", points: 100, perSecond: 200, style: "fiftyfifty" },
  u: { name: "Nosegrind", points: 150, perSecond: 250, style: "nosegrind" },
  d: { name: "5-0", points: 150, perSecond: 250, style: "fiveo" },
  l: { name: "Boardslide", points: 150, perSecond: 250, style: "boardslide" },
  r: { name: "Lipslide", points: 200, perSecond: 250, style: "lipslide" },
  ul: { name: "Crooked", points: 200, perSecond: 275, style: "crooked" },
  ur: { name: "Overcrook", points: 250, perSecond: 300, style: "overcrook" },
  dl: { name: "Smith", points: 200, perSecond: 275, style: "smith" },
  dr: { name: "Feeble", points: 200, perSecond: 275, style: "feeble" },
};

/**
 * How the board sits on the rail for each grind, in radians. `yaw` turns the
 * board (and rider) off the line of the rail, `pitch` > 0 tips it onto the
 * nose truck (< 0 the tail), `roll` dips one edge of the deck.
 */
export interface GrindPose {
  yaw: number;
  pitch: number;
  roll: number;
}

export const GRIND_POSES: Record<GrindStyle, GrindPose> = {
  fiftyfifty: { yaw: 0, pitch: 0, roll: 0 },
  nosegrind: { yaw: 0, pitch: 0.3, roll: 0 },
  fiveo: { yaw: 0, pitch: -0.3, roll: 0 },
  boardslide: { yaw: Math.PI / 2, pitch: 0, roll: 0 },
  lipslide: { yaw: Math.PI / 2, pitch: 0, roll: 0 },
  // Nose truck on the rail, the tail swung out to the heel side.
  crooked: { yaw: -0.4, pitch: 0.28, roll: 0.12 },
  // Nose truck, the board swung over the rail to the toe side.
  overcrook: { yaw: 0.4, pitch: 0.28, roll: -0.18 },
  // Tail truck, the nose dipped down past the heel side of the rail.
  smith: { yaw: 0.35, pitch: -0.28, roll: 0.28 },
  // Tail truck, the nose over the rail on the toe side.
  feeble: { yaw: -0.35, pitch: -0.28, roll: -0.22 },
  // Across the rail like a boardslide, the board upside down.
  darkslide: { yaw: Math.PI / 2, pitch: 0, roll: Math.PI },
};

export type LipStyle = "axle" | "invert" | "rock" | "disaster" | "nose";

/** A stall on the coping at the top of a vert launch (grind + direction), scoring over time. */
export interface LipTrick {
  name: string;
  points: number;
  perSecond: number;
  style: LipStyle;
  /** Comes back down the ramp rolling fakie (still facing up it) instead of turning to face down. */
  fakie: boolean;
}

/** THPS 3+4 lip tricks, picked by the stick when you reach the coping. */
export const LIP_TRICKS: Record<Dir, LipTrick> = {
  n: { name: "Axle Stall", points: 200, perSecond: 200, style: "axle", fakie: false },
  u: { name: "Invert", points: 400, perSecond: 250, style: "invert", fakie: false },
  d: { name: "Rock to Fakie", points: 250, perSecond: 200, style: "rock", fakie: true },
  l: { name: "Disaster", points: 300, perSecond: 225, style: "disaster", fakie: false },
  r: { name: "Nose Stall", points: 250, perSecond: 200, style: "nose", fakie: false },
};

/**
 * Rough poses on the coping until the pose system (#8): `yaw` turns the board
 * off the ramp's line, `pitch` tips it (> 0 nose up over the deck), `roll`
 * dips an edge, `invert` turns the rider upside down on one hand (0..1).
 */
export interface LipPose extends GrindPose {
  invert: number;
}

export const LIP_POSES: Record<LipStyle, LipPose> = {
  // Both trucks on the coping, the board along it.
  axle: { yaw: Math.PI / 2, pitch: 0, roll: 0, invert: 0 },
  // Handplant: upside down over the coping, board held overhead.
  invert: { yaw: 0, pitch: 0, roll: 0, invert: 1 },
  // Front truck over the coping, the board rocked onto the lip.
  rock: { yaw: 0, pitch: 0.35, roll: 0, invert: 0 },
  // Board across the coping, middle of the deck on it.
  disaster: { yaw: 0, pitch: -0.25, roll: 0.15, invert: 0 },
  // Nose on the coping, tail hanging down the ramp.
  nose: { yaw: 0, pitch: 0.7, roll: 0, invert: 0 },
};

export interface ManualTrick {
  name: string;
  points: number;
  perSecond: number;
  /** Balanced on the nose wheels instead of the tail. */
  nose: boolean;
}

/** THPS 2: up, down is a manual; down, up is a nose manual. */
export const MANUAL_TRICKS = {
  manual: { name: "Manual", points: 100, perSecond: 150, nose: false },
  nose: { name: "Nose Manual", points: 125, perSecond: 175, nose: true },
} satisfies Record<string, ManualTrick>;

/**
 * THPS 2 flatground tricks, done from a manual. A pop trick flips or spins
 * the board under you and lands back in the manual.
 */
export interface ManualPopTrick {
  name: string;
  points: number;
  anim: FlipAnim;
  /** Board rotations the animation turns through (a 360 Shove-It is two half turns). */
  rotations: number;
  /** Seconds the board is off the ground; no other ground trick until it's back. */
  duration: number;
}

/** Where the board goes for a held ground trick, relative to the manual pose. */
export interface ManualHoldPose {
  /** Board pitch, radians: < 0 nose up (onto the tail), > 0 nose down. */
  pitch: number;
  /** Board roll: PI is upside down, PI / 2 up on its edge. */
  roll: number;
  /** How far the rider rises to stay on top of the board. */
  lift: number;
  /** Which hand holds the board, if any. */
  hand: "front" | "back" | null;
}

/** A held ground trick: scores over time like a grab, for as long as you hold the button. */
export interface ManualHoldTrick {
  name: string;
  points: number;
  perSecond: number;
  pose: ManualHoldPose;
}

/**
 * Flip button + direction from a manual: the same diagonals as in the air,
 * but left is the 360 Shove-It, since a manual can't double-tap a shove.
 */
export const MANUAL_POP_TRICKS: Record<Dir8, ManualPopTrick> = {
  n: { name: "Kickflip", points: 100, anim: "kickflip", rotations: 1, duration: 0.34 },
  r: { name: "Heelflip", points: 100, anim: "heelflip", rotations: 1, duration: 0.34 },
  d: { name: "Shove-It", points: 75, anim: "shove", rotations: 1, duration: 0.28 },
  l: { name: "360 Shove-It", points: 150, anim: "shove", rotations: 2, duration: 0.38 },
  u: { name: "Impossible", points: 150, anim: "impossible", rotations: 1, duration: 0.4 },
  ul: { name: "Hardflip", points: 175, anim: "hardflip", rotations: 1, duration: 0.4 },
  ur: { name: "360 Flip", points: 200, anim: "treflip", rotations: 1, duration: 0.44 },
  dl: { name: "Varial Kickflip", points: 150, anim: "varial", rotations: 1, duration: 0.4 },
  dr: { name: "Varial Heelflip", points: 175, anim: "varialheel", rotations: 1, duration: 0.4 },
};

/** Grab button + direction from a manual (hold it). */
export const MANUAL_HOLD_TRICKS: Record<Dir, ManualHoldTrick> = {
  // Board flipped upside down, tail on the ground, front foot on the raised nose.
  n: { name: "Casper", points: 200, perSecond: 250, pose: { pitch: -0.45, roll: Math.PI, lift: 0.1, hand: null } },
  // The same upside down, on the nose instead.
  r: { name: "Anti-Casper", points: 200, perSecond: 250, pose: { pitch: 0.45, roll: Math.PI, lift: 0.1, hand: null } },
  // Stood straight up on the tail, holding the nose, hopping.
  u: { name: "Pogo", points: 250, perSecond: 300, pose: { pitch: -1.45, roll: 0, lift: 0.22, hand: "front" } },
  // Most of the way up on the tail, balanced on the back truck, arms out.
  d: { name: "Truckstand", points: 200, perSecond: 250, pose: { pitch: -1.0, roll: 0, lift: 0.14, hand: null } },
  // Board on its edge, feet on the side rail.
  l: { name: "Primo", points: 250, perSecond: 300, pose: { pitch: 0, roll: Math.PI / 2, lift: 0.12, hand: null } },
};

/** Longest gap (seconds) between two presses that still reads as one sequence. */
export const SEQUENCE_GAP = 0.35;

/**
 * A short history of stick presses, for THPS button sequences like up, down
 * (manual). Each axis is read on its own, so steering while tapping up and
 * down still counts. A press is the stick going past halfway in a direction.
 */
export class StickHistory {
  private presses: { dir: Dir; t: number }[] = [];
  private clock = 0;
  private prevX: Dir = "n";
  private prevY: Dir = "n";

  /** Feeds one step. Returns the two-press sequence this step completed, if any. */
  update(x: number, y: number, dt: number): [Dir, Dir] | null {
    this.clock += dt;
    const dx: Dir = x > 0.5 ? "r" : x < -0.5 ? "l" : "n";
    const dy: Dir = y > 0.5 ? "u" : y < -0.5 ? "d" : "n";
    let pressed = false;
    for (const [dir, prev] of [
      [dx, this.prevX],
      [dy, this.prevY],
    ] as const) {
      if (dir !== "n" && dir !== prev) {
        this.presses.push({ dir, t: this.clock });
        pressed = true;
      }
    }
    this.prevX = dx;
    this.prevY = dy;
    if (this.presses.length > 4) this.presses.splice(0, this.presses.length - 4);
    if (!pressed || this.presses.length < 2) return null;
    const a = this.presses[this.presses.length - 2]!;
    const b = this.presses[this.presses.length - 1]!;
    return b.t - a.t <= SEQUENCE_GAP ? [a.dir, b.dir] : null;
  }

  /** Forgets past presses, so a used sequence can't combine with the next press. */
  clear(): void {
    this.presses = [];
  }
}

/** Collapse an analog stick / d-pad reading into one of five THPS directions. */
export function stickDir(x: number, y: number, threshold = 0.5): Dir {
  if (Math.max(Math.abs(x), Math.abs(y)) < threshold) return "n";
  if (Math.abs(x) > Math.abs(y)) return x < 0 ? "l" : "r";
  return y > 0 ? "u" : "d";
}

const DIRS8: Dir8[] = ["r", "ur", "u", "ul", "l", "dl", "d", "dr"];

/**
 * How far off an axis the stick has to be for a diagonal: the smaller of x
 * and y at least this share of the bigger (about 27 degrees off the axis, so
 * each diagonal owns a 37 degree slice and each straight direction 53).
 */
export const DIAGONAL_RATIO = 0.5;

/** Like `stickDir`, but diagonals count, when the stick is clearly on one (`DIAGONAL_RATIO`). */
export function stickDir8(x: number, y: number, threshold = 0.5): Dir8 {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (Math.max(ax, ay) < threshold) return "n";
  if (Math.min(ax, ay) < DIAGONAL_RATIO * Math.max(ax, ay)) return ax > ay ? (x < 0 ? "l" : "r") : y > 0 ? "u" : "d";
  const slice = Math.round(Math.atan2(y, x) / (Math.PI / 4));
  return DIRS8[(slice + 8) % 8]!;
}

/** How long before a flip press the stick is read (#85), so a stick still moving doesn't pick the trick. */
export const FLIP_AIM_LOOKBACK = 0.06;

/**
 * Remembers the stick's last few readings so a flip can read where the stick
 * was `FLIP_AIM_LOOKBACK` before the press, when it's moving from one
 * direction to another as you press. A stick centered at the press is always
 * the neutral flip (you let go), and one centered back then reads where it is
 * now, so pressing a direction and the button together still works.
 */
export class FlipAim {
  private xs = new Float64Array(32);
  private ys = new Float64Array(32);
  private ts = new Float64Array(32);
  private count = 0;
  private clock = 0;

  /** Feeds this step's stick (call once per step, before reading `dir`). */
  update(x: number, y: number, dt: number): void {
    this.clock += dt;
    const i = this.count % this.xs.length;
    this.xs[i] = x;
    this.ys[i] = y;
    this.ts[i] = this.clock;
    this.count++;
  }

  /** The flip direction for a press this step. */
  dir(): Dir8 {
    if (this.count === 0) return "n";
    const n = this.xs.length;
    const last = (this.count - 1) % n;
    const now = stickDir8(this.xs[last]!, this.ys[last]!);
    if (now === "n") return now;
    const cutoff = this.clock - FLIP_AIM_LOOKBACK + 1e-9;
    for (let k = 1; k < Math.min(this.count, n); k++) {
      const i = (this.count - 1 - k) % n;
      if (this.ts[i]! <= cutoff) {
        const then = stickDir8(this.xs[i]!, this.ys[i]!);
        return then === "n" ? now : then;
      }
    }
    return now;
  }

  clear(): void {
    this.count = 0;
  }
}

/** Rotation completed in the air, rounded to the nearest 180 with a little forgiveness. */
export function spinDegrees(radians: number): number {
  const deg = (Math.abs(radians) * 180) / Math.PI;
  return Math.floor((deg + 35) / 180) * 180;
}

export interface ComboEntry {
  name: string;
  points: number;
}

/**
 * A combo is every trick since you last had four wheels on the ground. Its value
 * is (sum of trick points) x (number of tricks). Repeating a trick inside one
 * combo is worth progressively less, like THPS.
 */
export class Combo {
  entries: ComboEntry[] = [];
  /** How much of the combo's value has already gone into the special meter (#184). */
  fed = 0;
  private repeats = new Map<string, number>();

  get base(): number {
    return this.entries.reduce((sum, e) => sum + e.points, 0);
  }

  get multiplier(): number {
    return this.entries.length;
  }

  get value(): number {
    return this.base * this.multiplier;
  }

  get active(): boolean {
    return this.entries.length > 0;
  }

  /** Adds a trick and returns its index so timed tricks can keep growing. */
  add(name: string, points: number): number {
    const seen = this.repeats.get(name) ?? 0;
    this.repeats.set(name, seen + 1);
    const decay = Math.max(0.25, 1 - 0.25 * seen);
    this.entries.push({ name, points: Math.round(points * decay) });
    return this.entries.length - 1;
  }

  addPoints(index: number, points: number): void {
    const e = this.entries[index];
    if (e) e.points += points;
  }

  rename(index: number, name: string): void {
    const e = this.entries[index];
    if (e) e.name = name;
  }

  /** The value scored since the last feed, for the special meter to fill with as the combo goes (#184). */
  feed(): number {
    const more = Math.max(0, this.value - this.fed);
    this.fed += more;
    return more;
  }

  /** Lands the combo: returns its value and resets. */
  bank(): number {
    const v = Math.round(this.value);
    this.reset();
    return v;
  }

  reset(): void {
    this.entries = [];
    this.fed = 0;
    this.repeats.clear();
  }

  describe(): string {
    return this.entries.map((e) => e.name).join(" + ");
  }
}
